import React from 'react';
import { Table, Card, Grid, Empty, Spin, Pagination } from 'antd';

const { useBreakpoint } = Grid;

/**
 * Geniş ekranda TABLO, telefonda KART listesi.
 *
 * NEDEN: veli mobil uygulaması henüz herkeste değil (iOS için D-U-N-S
 * bekleniyor), veliler web arayüzünü telefondan açıyor. Tablolar
 * `scroll={{ x }}` ile sayfayı bozmuyor ama 5 sütunlu bir yoklama tablosunu
 * 375px'te parmakla sağa sola kaydırarak okumak yorucu.
 *
 * NEDEN TEK BİLEŞEN: her sayfaya "mobilde şu, masaüstünde bu" diye İKİ
 * ayrı görünüm yazmak, aynı sütun tanımının iki kopyasını doğurur ve o
 * kopyalar zamanla birbirinden sapar. Bu projede tam olarak bu yüzden
 * canlıda hata çıktı (kaybolan ders, doluluk sayımı). Burada sütun tanımı
 * TEK: aynı `columns` dizisi hem tabloyu hem kartı üretiyor, `render`
 * fonksiyonları da aynen çalışıyor.
 *
 * MASAÜSTÜ DAVRANIŞI DEĞİŞMİYOR — md ve üstünde birebir aynı `<Table>`.
 *
 * Kullanım: mevcut `<Table ... />` çağrısını `<ResponsiveTable ... />`
 * yapmak yeterli, başka bir şey gerekmiyor.
 */

/** Sütunun bu satırdaki gösterilecek değeri (render varsa ondan geçerek). */
const hucre = (col, kayit, sira) => {
  const ham = col.dataIndex ? kayit[col.dataIndex] : undefined;
  return col.render ? col.render(ham, kayit, sira) : ham;
};

const satirAnahtari = (rowKey, kayit, sira) => {
  if (typeof rowKey === 'function') return rowKey(kayit, sira);
  return kayit[rowKey] ?? sira;
};

const ResponsiveTable = ({
  columns = [],
  dataSource = [],
  rowKey = 'id',
  loading = false,
  pagination,
  ...rest
}) => {
  const ekran = useBreakpoint();

  // md = 768px. Tablet ve üstünde tablo daha okunaklı; kart yalnızca
  // telefonda devreye giriyor.
  if (ekran.md) {
    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={rowKey}
        loading={loading}
        pagination={pagination}
        {...rest}
      />
    );
  }

  if (loading) {
    return <div className="text-center py-6"><Spin /></div>;
  }

  if (!dataSource.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Kayıt yok" />;
  }

  // Başlıksız sütunlar işlem sütunudur (indir, görüntüle, iptal et gibi
  // düğmeler). Kartın gövdesinde "  : [düğme]" diye durmaları anlamsız,
  // altta ayrı bir satırda toplanıyorlar.
  const islemler = columns.filter(c => !c.title);
  const alanlar  = columns.filter(c => c.title);

  // İlk anlamlı sütun kartın başlığı olur — veli listeye baktığında önce
  // "hangi kayıt" sorusunu soruyor (tarih, dosya adı, tür).
  const [baslikKolonu, ...digerleri] = alanlar;

  return (
    <div>
      {dataSource.map((kayit, sira) => (
        <Card
          key={satirAnahtari(rowKey, kayit, sira)}
          size="small"
          className="mb-2"
          styles={{ body: { padding: 12 } }}
        >
          {baslikKolonu && (
            <div className="font-semibold text-sm mb-2">
              {hucre(baslikKolonu, kayit, sira)}
            </div>
          )}

          {digerleri.map(col => {
            const deger = hucre(col, kayit, sira);
            // Boş alanı satır olarak göstermek kartı gereksiz uzatıyor.
            if (deger === null || deger === undefined || deger === '') return null;

            return (
              <div key={col.key || col.dataIndex || col.title} className="flex justify-between gap-3 py-0.5 text-sm">
                <span className="text-gray-500 shrink-0">{col.title}</span>
                <span className="text-right">{deger}</span>
              </div>
            );
          })}

          {islemler.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
              {islemler.map((col, i) => (
                <React.Fragment key={col.key || i}>{hucre(col, kayit, sira)}</React.Fragment>
              ))}
            </div>
          )}
        </Card>
      ))}

      {/* Sayfalama SUNUCU TARAFLI (onChange veri çekiyor) — nesneyi olduğu
          gibi geçiyoruz, burada dilimleme yapılmıyor. */}
      {pagination && (
        <div className="flex justify-center mt-3">
          <Pagination size="small" {...pagination} />
        </div>
      )}
    </div>
  );
};

export default ResponsiveTable;
